import { Suspense, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './core/auth';
import { Layout } from './core/Layout';
import { LoginPage } from './core/LoginPage';
import { OnboardingTour } from './core/OnboardingTour';
import { OnboardingActiveContext } from './core/onboardingContext';
import { visibleModules } from './modules';

/** 사용자별 온보딩 1회 표시. 끝까지 보거나 건너뛰면 기록. */
const onboardingKey = (userId: string) => `us:onboarding-seen:${userId}`;

function Loader() {
  return (
    <div className="flex min-h-full items-center justify-center bg-surface">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-accentA" />
    </div>
  );
}

function AppRoutes() {
  const { boot } = useAuth();
  const mods = visibleModules(boot!.me.isAdmin);
  return (
    <Routes>
      <Route element={<Layout />}>
        {mods.map((m) => {
          const C = m.Component;
          return (
            <Route
              key={m.id}
              path={m.route}
              element={
                <Suspense fallback={<Loader />}>
                  <C />
                </Suspense>
              }
            />
          );
        })}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function AppShell() {
  const { boot } = useAuth();
  // 온보딩은 사용자별 1회. 진행 중에는 컨텍스트로 알려 하단탭 등 스포일러를 숨긴다.
  const [seen, setSeen] = useState(() => localStorage.getItem(onboardingKey(boot!.me.id)) != null);
  // 어드민 미리보기: seen이어도 임시로 온보딩을 띄운다(문구 override 전달).
  const [preview, setPreview] = useState<string | null>(null);
  const active = !seen || preview !== null;

  return (
    <OnboardingActiveContext.Provider value={{ active, startPreview: (w) => setPreview(w) }}>
      <AppRoutes />
      {active && (
        <OnboardingTour
          welcomeOverride={preview ?? undefined}
          onDone={() => {
            if (preview !== null) {
              setPreview(null); // 미리보기는 seen을 건드리지 않는다.
            } else {
              localStorage.setItem(onboardingKey(boot!.me.id), '1');
              setSeen(true);
            }
          }}
        />
      )}
    </OnboardingActiveContext.Provider>
  );
}

export function App() {
  const { boot, loading } = useAuth();
  if (loading) return <Loader />;
  if (!boot) return <LoginPage />;
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
